import CustomToast from "components/atoms/CustomToast";
import Button from "components/common/buttons/buttons";
import { FixedButtonContainer } from "components/common/buttons/FixedButtons";
import TopHeader from "components/common/header";
import { useRouter } from "next/router";
import { useCallback, useEffect, useRef, useState } from "react";
import * as Sentry from "@sentry/nextjs";
import { GroupedSurveyMeta, groupSurveyMeta } from "utils/portfolio/surveyMeta";
import Step2 from "./Step2";
import Step3 from "./Step3";
import { useRetireSurvey } from "utils/clientAPIs/saa";
import Nonmember from "../util/nonmember";
import { useSession } from "next-auth/react";
import PortfolioResultLoading from "../../Portfolio/ResultLoading";
import { isNumberInvalid } from "../util/result/format";
import {
  ensureStepGuard,
  writeProgress,
  clearProgress,
  resolveUserKey,
  readProgress,
} from "../util/progress";
import Step1 from "../util/survey/Step1";
import Step0 from "../util/survey/Step0";
import { SurveyError } from "../util/Error";
import SaaLoading from "../util/Loading";
import {
  PlannerStepKey,
  usePlannerCompute,
  usePlannerStep,
} from "lib/analytics/react/hooks";
import { getStepKey } from "../consume/Index";
import { trackPageAction } from "lib/analytics/core/actions";

interface SurveyData {
  saving_amount_monthly: number | null; // 월 저축액
  saving_total: number | null; // 모은돈
  retire_lifestyle_amount: number | null; // 소비 카테고리
  retire_timing: string; // 소비시점
}
function SaaRetireIndex({
  surveyList,
  isMobile,
}: {
  surveyList: any;
  isMobile: boolean;
}) {
  // 기본
  const router = useRouter();
  const plannerStep = usePlannerStep();
  const plannerCompute = usePlannerCompute();
  const { data: session } = useSession();
  const stepQuery = router.query?.step;
  const subQuery = router.query?.sub;
  const abQuery = router.query?.ab;
  const currentStep = Array.isArray(stepQuery)
    ? parseInt((stepQuery?.[0] as string) || "1", 10) || 1
    : parseInt(((stepQuery as string) || "1") as string, 10) || 1;

  const gotoStep = useCallback(
    (step: number) => {
      const stepKeyString = getStepKey(step - 1);
      plannerStep.submit({
        step_key: stepKeyString as PlannerStepKey,
        step_index: step,
        outcome: "ok",
      });
      router.replace(
        {
          pathname: router.pathname,
          query: { ...router.query, step: String(step), ab: abQuery },
        },
        undefined,
        { shallow: true }
      );
    },
    [router]
  );

  // 안정적인 hashId를 1회 생성/재사용
  const nonmemberRef = useRef<Nonmember | null>(null);
  const hashId = (session as any)?.user?.email
    ? ""
    : (nonmemberRef.current ??= new Nonmember()).getHashId() || "";

  useEffect(() => {
    if (!router.isReady) return;
    if (!router.query?.step) {
      gotoStep(1);
    }
    const userKey = resolveUserKey((session as any)?.user?.email);
    ensureStepGuard("retire", userKey, currentStep, gotoStep);
  }, [
    router.isReady,
    router.query?.step,
    currentStep,
    gotoStep,
    (session as any)?.user?.email,
  ]);

  // 저장된 답변 복원 (최초 1회)
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (!router.isReady || hydratedRef.current) return;
    const userKey = resolveUserKey((session as any)?.user?.email);
    const prog = readProgress("retire", userKey);
    if (prog?.answers) {
      setSurveyData((prev) => ({
        ...prev,
        saving_amount_monthly: null,
        saving_total: null,
        ...(prog.answers as Partial<SurveyData>),
      }));
    }
    hydratedRef.current = true;
  }, [router.isReady, (session as any)?.user?.email]);

  // 스텝 진입
  useEffect(() => {
    plannerStep.view({
      step_key: getStepKey(currentStep) as PlannerStepKey,
      step_index: currentStep + 1,
    });
  }, [currentStep]);

  // DB 설문 메타 그룹핑
  const grouped: GroupedSurveyMeta = groupSurveyMeta(surveyList);
  const step3Meta = grouped["retire_timing"];

  const [surveyData, setSurveyData] = useState<SurveyData>({
    saving_amount_monthly: null,
    saving_total: null,
    retire_lifestyle_amount: null,
    retire_timing: step3Meta?.options?.[0]?.answer_id,
  });

  // 서베이 적재
  const {
    mutate,
    isPending,
    isError,
    isPendingCalc,
    isErrorCalc,
    isPendingResultSave,
    isErrorResultSave,
    isGreatEnough,
    isTooHigh,
    isTimeout,
    recommandTargetAmount,
  } = useRetireSurvey({ plannerCompute });

  const [isErrorToast, setIsErrorToast] = useState(false);
  useEffect(() => {
    if (isError || isErrorResultSave || isErrorCalc) {
      Sentry.captureException("SAA Consume 서베이 적재 실패", {
        extra: {
          surveyData,
          isError,
          isErrorResultSave,
          isErrorCalc,
        },
      });
      setIsErrorToast(true);
    }
  }, [isError, isErrorResultSave, isErrorCalc]);

  const handleNext = useCallback(() => {
    if (currentStep < 4) {
      const userKey = resolveUserKey((session as any)?.user?.email);
      writeProgress("retire", userKey, {
        completedStep: currentStep,
        answers: surveyData as unknown as Record<string, unknown>,
      });
      gotoStep(currentStep + 1);
    }
  }, [currentStep, gotoStep, (session as any)?.user?.email, surveyData]);

  const handleBack = useCallback(() => {
    if (subQuery) {
      router.replace(
        {
          pathname: router.pathname,
          query: { ...router.query, sub: undefined },
        },
        undefined,
        { shallow: true }
      );
      return;
    }
    if (currentStep > 1) {
      gotoStep(currentStep - 1);
    } else {
      router.back();
    }
  }, [currentStep, gotoStep, router]);

  const handleMonthSavings = useCallback((option: number) => {
    trackPageAction("input_planner_survey");
    setSurveyData((prev) => ({ ...prev, saving_amount_monthly: option }));
  }, []);
  const handleTotalSavings = useCallback((option: number) => {
    trackPageAction("input_planner_survey");
    setSurveyData((prev) => ({ ...prev, saving_total: option }));
  }, []);
  const handleStep2 = useCallback((option: number | null) => {
    trackPageAction("input_planner_survey");
    setSurveyData((prev) => ({ ...prev, retire_lifestyle_amount: option }));
  }, []);

  const handleStep3 = useCallback((option: string) => {
    trackPageAction("select_planner_survey");
    setSurveyData((prev) => ({ ...prev, retire_timing: option }));
  }, []);

  const handleComplete = useCallback(
    (recommandTargetAmount?: number) => {
      if (!recommandTargetAmount) {
        plannerStep.submit({
          step_key: "q_savings_monthly",
          step_index: 5,
          outcome: "ok",
        });
      }

      // TODO: 설문 데이터를 서버로 전송하고 결과 페이지로 이동
      const stableHashId = hashId;
      const newAnswer = recommandTargetAmount
        ? {
            ...surveyData,
            retire_lifestyle_amount: Math.floor(recommandTargetAmount / 10000),
          }
        : surveyData;
      setSurveyData(newAnswer);
      mutate({
        answer: newAnswer,
        hashId: stableHashId,
        type: "retire",
        email: session?.user?.email || "",
        isRecommend: recommandTargetAmount ? true : false,
      });
      const userKey = resolveUserKey((session as any)?.user?.email);
      clearProgress("retire", userKey);
    },
    [surveyData, session, recommandTargetAmount, hashId]
  );

  // 유효성 검사

  const isInvalidStep0 = isNumberInvalid(surveyData.saving_total);
  const isInvalidStep1 = isNumberInvalid(surveyData.saving_amount_monthly);

  const isInvalidStep2 = isNumberInvalid(surveyData.retire_lifestyle_amount);
  const isInvalidStep3 = !surveyData.retire_timing;

  const isAnyPending = isPending || isPendingCalc || isPendingResultSave;
  const isDisabledByStep =
    currentStep === 1
      ? isInvalidStep2
      : currentStep === 2
      ? isInvalidStep3
      : currentStep === 3
      ? isInvalidStep0
      : currentStep === 4
      ? isInvalidStep1
      : true;
  const isDisabled =
    isAnyPending || (currentStep === 1 && !!subQuery) || isDisabledByStep;

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <Step2
            value={surveyData.retire_lifestyle_amount}
            handleValue={handleStep2}
          />
        );
      case 2:
        return (
          <Step3
            selectedOption={surveyData.retire_timing}
            onOptionSelect={handleStep3}
            surveyList={surveyList}
          />
        );
      case 3:
        return (
          <Step0
            saving_total={surveyData.saving_total}
            handleTotalSavings={handleTotalSavings}
            type="retire"
          />
        );
      case 4:
        return (
          <Step1
            saving_amount_monthly={surveyData.saving_amount_monthly}
            handleMonthSavings={handleMonthSavings}
            type="retire"
          />
        );
      default:
        return <div></div>;
    }
  };

  if (isPendingCalc || isPending || isPendingResultSave) {
    return (
      <SaaLoading isMobile={isMobile} surveyData={surveyData} type="retire" />
    );
  }

  console.log("recommandTargetAmount", recommandTargetAmount);
  if (
    isGreatEnough ||
    isTooHigh ||
    isError ||
    isErrorResultSave ||
    isErrorCalc ||
    isTimeout
  ) {
    return (
      <SurveyError
        status={
          isTooHigh
            ? "tooHigh"
            : isGreatEnough
            ? "greatEnough"
            : isTimeout
            ? "timeout"
            : "error"
        }
        isMobile={isMobile}
        surveyData={surveyData}
        handleBack={handleBack}
        type="retire"
        hashId={hashId || undefined}
        retryHandler={() => {
          mutate({
            answer: surveyData,
            hashId: hashId,
            type: "consume",
            email: session?.user?.email || "",
          });
        }}
        recommandHandler={handleComplete}
        recommandTargetAmount={recommandTargetAmount}
        plannerStep={plannerStep}
      />
    );
  }

  return (
    <div
      className="w-full "
      style={{
        height: isMobile ? "100dvh" : "calc(100vh - 80px)",
      }}
    >
      {/* Top Bar */}
      {isPendingCalc || isPending || isPendingResultSave ? (
        <PortfolioResultLoading />
      ) : (
        <>
          <TopHeader clickHandler={handleBack} />
          {renderCurrentStep()}
        </>
      )}
      {/* Bottom Button */}
      <FixedButtonContainer
        isMobile={isMobile}
        customStyle={{
          background: "transparent",
          display: currentStep === 1 && !!subQuery ? "none" : "block",
        }}
      >
        <div className="relative">
          <Button
            size="lg"
            text={"다음"}
            clickHandler={
              isPending
                ? null
                : currentStep === 1 && !!subQuery
                ? null
                : currentStep < 4
                ? handleNext
                : handleComplete
            }
            type="primary"
            btnClass="w-full relative"
            disabled={isDisabled}
            dataGtmId={
              currentStep === 1
                ? "saa-survey-step1-btn"
                : currentStep === 2
                ? "saa-survey-step2-btn"
                : currentStep === 3
                ? "saa-survey-step3-btn"
                : currentStep === 4
                ? "saa-survey-step4-btn"
                : ""
            }
            dataTestId="saa-retire-survey-next-btn"
          />
        </div>
      </FixedButtonContainer>

      <CustomToast
        message="서버 오류가 발생했어요"
        isVisible={isErrorToast}
        onClose={() => setIsErrorToast(false)}
        isSuccess={false}
        width={260}
      />
    </div>
  );
}

export default SaaRetireIndex;
